import { forwardRef, ComponentProps, useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { parseMoneyInput } from "@/lib/money/parseMoneyInput";

interface CurrencyInputProps extends Omit<ComponentProps<"input">, 'onChange' | 'value' | 'type'> {
  value: number;
  onChange: (value: number) => void;
}

// PDV-CAMPO-PAGAMENTO (I.160): vírgula e ponto valem ("1.000" = mil reais; "20.50" = 20,50 — regra em parseMoneyInput);
// ao focar, o valor fica todo selecionado na hora e o clique que focou não desfaz a seleção (a digitação substitui).
const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, value, onChange, onMouseUp, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const keepSelectionOnMouseUp = useRef(false);

    useEffect(() => {
      // Não sobrescrever enquanto estiver editando
      if (isEditing) return;

      const currentNumeric = parseMoneyInput(displayValue);
      if (value !== undefined && value !== null && Math.abs(value - currentNumeric) > 0.01) {
        setDisplayValue(formatCurrency(value));
      }
    }, [value, isEditing]);

    const formatCurrency = (num: number): string => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.currentTarget?.value ?? e.target?.value ?? '';

      // Permitir apenas números, vírgula e ponto (o ponto não é mais descartado)
      const sanitized = inputValue.replace(/[^\d.,]/g, '');

      // Atualizar display SEM formatação completa durante digitação
      setDisplayValue(sanitized);

      // Converter para número e notificar onChange
      onChange(parseMoneyInput(sanitized));
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(true);
      // Ao focar, mostrar apenas o número para facilitar edição
      const numValue = parseMoneyInput(displayValue);
      if (numValue > 0) {
        setDisplayValue(numValue.toFixed(2).replace('.', ','));
      } else {
        setDisplayValue('');
      }

      // Selecionar todo o texto já no próximo quadro (depois de o React trocar o texto) e segurar a seleção contra o
      // clique/toque que deu o foco; rolar para o centro só depois (teclado do celular)
      const el = e.currentTarget;
      keepSelectionOnMouseUp.current = true;
      requestAnimationFrame(() => {
        if (el?.isConnected && document.activeElement === el) el.select();
      });
      setTimeout(() => {
        if (!el?.isConnected) return;
        if (document.activeElement === el && keepSelectionOnMouseUp.current) el.select();
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      props.onFocus?.(e);
    };

    const handleMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
      if (keepSelectionOnMouseUp.current) {
        e.preventDefault();
        keepSelectionOnMouseUp.current = false;
        e.currentTarget.select();
      }
      onMouseUp?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsEditing(false);
      keepSelectionOnMouseUp.current = false;
      // Ao perder foco, formatar completamente
      const numValue = parseMoneyInput(displayValue);
      setDisplayValue(formatCurrency(numValue));
      props.onBlur?.(e);
    };

    const { onFocus: _onFocus, onBlur: _onBlur, ...rest } = props;

    return (
      <input
        type="text"
        inputMode="decimal"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className,
        )}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMouseUp={handleMouseUp}
        ref={ref}
        {...rest}
      />
    );
  },
);
CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
